import React from "react";
import { UtensilsCrossed, Coffee, Shirt, ShoppingBag, ExternalLink, Phone, MessageCircle, Utensils } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SectionLayout, SectionIntro } from "@/components/SectionLayout";

const MapBtn = ({ url, icon: Icon, label, testid }) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="maqam-card group flex items-center gap-3 rounded-2xl p-4 transition-transform duration-300 hover:-translate-y-0.5"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0A192F]/6 text-[#0A192F] transition-colors group-hover:bg-[#0A192F] group-hover:text-[#D4AF37]">
        <Icon size={18} strokeWidth={1.7} />
      </span>
      <span className="min-w-0 flex-1 font-display text-sm font-bold text-[#0A192F]">{label}</span>
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-[#D4AF37]">
        <ExternalLink size={13} />
      </span>
    </a>
  ) : null;

const LaundryRow = ({ c, testid }) => (
  <div className="maqam-card flex items-center gap-3 rounded-2xl p-4" data-testid={testid}>
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0A192F]/6 text-[#0A192F]">
      <Shirt size={18} strokeWidth={1.7} />
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-display text-sm font-bold text-[#0A192F]">{c.name}</p>
      <p className="truncate text-xs text-slate-500">{c.phone}</p>
    </div>
    <a href={`tel:${String(c.phone).replace(/\s/g, "")}`} className="grid h-9 w-9 place-items-center rounded-full bg-[#0A192F] text-white active:scale-95"><Phone size={15} /></a>
    {c.whatsapp && (
      <a href={`https://wa.me/${String(c.whatsapp).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-full bg-[#128C7E] text-white active:scale-95"><MessageCircle size={15} /></a>
    )}
  </div>
);

const CityBlock = ({ city, data, t, L }) => {
  if (!data) return null;
  return (
    <div className="mb-7" data-testid={`food-city-${city}`}>
      <h2 className="mb-3 font-display text-xl font-bold text-[#0A192F]">{city === "madinah" ? t("prayer.madinah") : t("prayer.makkah")}</h2>
      <div className="space-y-2.5">
        <MapBtn url={data.restaurantsUrl} icon={Utensils} label={t("food.restaurants")} testid={`food-${city}-restaurants`} />
        <MapBtn url={data.cafesUrl} icon={Coffee} label={t("food.cafes")} testid={`food-${city}-cafes`} />
        {(Array.isArray(data.shopping) ? data.shopping : data.shopping ? [data.shopping] : []).map((s, i) => (
          <a
            key={i}
            href={s.mapsUrl || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid={`food-${city}-shopping-${i}`}
            className="maqam-card group flex items-center gap-3 rounded-2xl p-4 transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0A192F]/6 text-[#0A192F] transition-colors group-hover:bg-[#0A192F] group-hover:text-[#D4AF37]">
              <ShoppingBag size={18} strokeWidth={1.7} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-bold text-[#0A192F]">{L(s.name)}</p>
              {s.note && L(s.note) ? <p className="text-xs text-slate-500">{L(s.note)}</p> : null}
            </div>
            <ExternalLink size={13} className="shrink-0 text-[#D4AF37]" />
          </a>
        ))}
        {(data.laundry || []).map((c, i) => (
          <LaundryRow key={i} c={c} testid={`food-${city}-laundry-${i}`} />
        ))}
      </div>
    </div>
  );
};

export const FoodShopping = ({ data }) => {
  const { L, t } = useLang();
  if (!data) return null;

  // Fallback: legacy category shape
  if (!data.madinah && !data.makkah && Array.isArray(data.categories)) {
    return (
      <SectionLayout id="food" icon={UtensilsCrossed}>
        <SectionIntro>{L(data.intro)}</SectionIntro>
        <div className="space-y-3">
          {data.categories.map((c) => (
            <div key={c.id} className="maqam-card rounded-3xl p-5">
              <h3 className="mb-2 font-display text-lg font-bold text-[#0A192F]">{L(c.title)}</h3>
              <ul className="space-y-1.5">
                {(c.items || []).map((item, i) => (
                  <li key={i} className="text-sm text-[#0A1128]/75">{L(item)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </SectionLayout>
    );
  }

  return (
    <SectionLayout id="food" icon={UtensilsCrossed}>
      <SectionIntro>{L(data.intro)}</SectionIntro>
      <CityBlock city="madinah" data={data.madinah} t={t} L={L} />
      <CityBlock city="makkah" data={data.makkah} t={t} L={L} />
      {data.laundryNote && <p className="text-center text-xs text-slate-400">{L(data.laundryNote)}</p>}
    </SectionLayout>
  );
};
